import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Text,
  Box,
  FormHelperText,
  FormErrorMessage,
  Flex,
  Icon,
  useTheme,
} from "@chakra-ui/react";
import React, { useState, useEffect, useContext } from "react";
import { appRequest } from "routes";
import { FiFile, FiX } from "react-icons/fi";
import { AppContext } from "AppProvider/AppContext";
import { routePath } from "config/pathConstant";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { FaChevronDown } from "react-icons/fa";

interface PaymentUtrDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRow: any;
  onSuccess: () => void;
  docId?: any
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const PaymentUtrDrawer: React.FC<PaymentUtrDrawerProps> = ({
  isOpen,
  onClose,
  selectedRow,
  onSuccess,
  docId
}) => {
  const { accountDetails } = useContext(AppContext)
  const [utrNumber, setUtrNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [bankName, setBankName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [utrError, setUtrError] = useState("");
  const toast = useToast();
  const theme = useTheme();

  const [bankOptions, setBankOptions] = useState([
    { label: "BANK OF MAHARASHTRA", value: "BANK OF MAHARASHTRA" },
    { label: "AXIS BANK", value: "AXIS BANK" },
  ]);

  const customStyle = {
    control: (provided: any, state: any) => ({
      ...provided,
      width: "100%",
      maxHeight: "10rem",
      "min-height": "38px",
      border: state.isFocused ? "2px solid #319795" : "1px solid #e2e8f0",
      "&:hover": {
        borderColor: "#cbd5e0",
        cursor: "pointer",
      },
      color: "black",
      fontSize: "16px",
      fontWeight: "400",
      paddingLeft: "0.2rem",
      boxShadow: "none",
      backgroundColor: "#f7fafc",
      outline: "none",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "2px 8px",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "gray.400",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: "sm",
      "&:hover": {
        backgroundColor: "#E6FFFA",
        cursor: "pointer",
      },
      backgroundColor: state.isSelected ? "#319795" : "transparent",
      color: state.isSelected ? "white" : "black",
    }),
  };

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <FaChevronDown size="12px" />
      </components.DropdownIndicator>
    );
  };

  useEffect(() => {
    const savedBanks = localStorage.getItem("custom_bank_names");
    if (savedBanks) {
      try {
        const parsed = JSON.parse(savedBanks);
        if (Array.isArray(parsed)) {
          setBankOptions(prev => {
            const existingValues = new Set(prev.map(b => b.value));
            const newBanks = parsed.filter(b => !existingValues.has(b.value));
            return [...prev, ...newBanks];
          });
        }
      } catch (e) {
        console.error("Failed to load banks", e);
      }
    }
  }, []);

  const handleCreateBank = (inputValue: string) => {
    const upperVal = inputValue.toUpperCase().trim();
    if (!upperVal) return;
    
    const newOption = { label: upperVal, value: upperVal };
    setBankOptions((prev) => [...prev, newOption]);
    setBankName(upperVal);

    const savedBanks = localStorage.getItem("custom_bank_names");
    let banksToSave = savedBanks ? JSON.parse(savedBanks) : [];
    if (!banksToSave.some((b: any) => b.value === upperVal)) {
      banksToSave.push(newOption);
      localStorage.setItem("custom_bank_names", JSON.stringify(banksToSave));
    }
  };

  // Optional: pre-fill if you already have UTR (uncomment if backend sends it)
  // useEffect(() => {
  //   if (selectedRow?.paymentUtrNumber) {
  //     setUtrNumber(selectedRow.paymentUtrNumber);
  //   }
  // }, [selectedRow]);

  const validateUtr = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "UTR Number is required";
    if (trimmed.length < 8) return "UTR should be at least 8 characters";
    // You can add more strict regex if your bank/UPI UTR has fixed format
    // Example: /^[A-Za-z0-9]{12,22}$/  ← common for many Indian banks
    return "";
  };

  const handleUtrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUtrNumber(value);
    setUtrError(validateUtr(value));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum allowed size is 10MB",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!["image/jpeg", "image/png", "application/pdf"].includes(selectedFile.type)) {
      toast({
        title: "Invalid file type",
        description: "Only JPG, PNG, and PDF files are allowed",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
  };

  const removeFile = () => {
    setFile(null);
    setFileName("");
  };

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });




  const handleSubmit = async () => {
    const utrValidation = validateUtr(utrNumber);
    if (utrValidation) {
      setUtrError(utrValidation);
      return;
    }

    if (!file) {
      toast({
        title: "Missing file",
        description: "Please upload payment proof (image or PDF)",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const base64File = await toBase64(file);

      const payload = {
        documentId: selectedRow?._id || selectedRow?.documentId,
        utrNumber: utrNumber.trim(),
        paymentDate: paymentDate,
        bankName: bankName.trim(),
        file: base64File,
        fileName: file.name,
        mimeType: file.type
      };

      const response: any = await appRequest(
        "document",
        "paymentUtrStatusUpdate",
        payload
      );

      if (response?.status === "success" || response?.success) {
        toast({
          title: "Payment Updated",
          description: "UTR and proof uploaded successfully",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        onSuccess();
        handleClose();
      } else {
        throw new Error(response?.message || "Update failed");
      }
    } catch (err: any) {
      console.error("Payment update failed:", err);
      toast({
        title: "Update Failed",
        description: err.message || "Could not update payment details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayValue = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object" && "label" in val) return String(val.label);
    if (typeof val === "object" && "value" in val) return String(val.value);
    return String(val);
  };

  const handleClose = () => {
    setUtrNumber("");
    setPaymentDate("");
    setBankName("");
    setFile(null);
    setFileName("");
    setUtrError("");
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" bg="gray.50" color="teal.700">
          Update Payment Details
        </DrawerHeader>

        <DrawerBody px={6} py={6}>
          <Box
            mb={8}
            p={5}
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
          >
            <Text
              textTransform="uppercase"
              letterSpacing="wider"
              fontSize="xs"
              fontWeight="bold"
              color="gray.500"
              mb={3}
            >
              Document Summary
            </Text>
            <VStack align="stretch" spacing={2}>
              <Flex justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  Vendor Name
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {getDisplayValue(selectedRow?.originalValues?.vendorName || selectedRow?.vendorName || "—")}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  Invoice No.
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {getDisplayValue(selectedRow?.invoiceNumber ||
                    selectedRow?.originalValues?.invoiceNo ||
                    "—")}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">
                  Invoice Amount
                </Text>
                <Text fontSize="md" fontWeight="bold" color="teal.600">
                  {getDisplayValue(selectedRow?.originalValues?.currency) || "₹"}{" "}
                  {Number(
                    getDisplayValue(selectedRow?.originalValues?.Invoice_Amount || selectedRow?.originalValues?.grandTotal || selectedRow?.originalValues?.invoiceAmount || 0),
                  ).toLocaleString("en-IN")}
                </Text>
              </Flex>
            </VStack>
          </Box>

          <VStack spacing={5}>
            <FormControl isRequired>
              <FormLabel fontWeight="600" color="gray.700">
                Bank Name
              </FormLabel>
              <CreatableSelect
                isClearable
                placeholder="Select or type bank name..."
                options={bankOptions}
                value={bankName ? { label: bankName, value: bankName } : null}
                onChange={(option: any) => setBankName(option ? option.value : "")}
                onCreateOption={handleCreateBank}
                styles={customStyle}
                components={{
                  IndicatorSeparator: null,
                  DropdownIndicator,
                }}
              />
            </FormControl>

            <FormControl isRequired isInvalid={!!utrError}>
              <FormLabel fontWeight="600" color="gray.700">
                Bank Reference Number
              </FormLabel>
              <Input
                value={utrNumber}
                onChange={handleUtrChange}
                placeholder="e.g. HDFC000012345678"
                autoFocus
                focusBorderColor="teal.500"
                variant="filled"
                bg="gray.50"
                _hover={{ bg: "gray.100" }}
              />
              {utrError ? (
                <FormErrorMessage>{utrError}</FormErrorMessage>
              ) : (
                <FormHelperText fontSize="xs">
                  Enter the Reference number from your bank statement
                </FormHelperText>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontWeight="600" color="gray.700">
                Payment Date
              </FormLabel>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]} // Prevent future dates
                focusBorderColor="teal.500"
                variant="filled"
                bg="gray.50"
                _hover={{ bg: "gray.100" }}
              />
            </FormControl>


            <FormControl isRequired mt={2}>
              <FormLabel fontWeight="600" color="gray.700">
                Upload Payment Proof
              </FormLabel>

              {!file ? (
                <Box
                  border="2px dashed"
                  borderColor="gray.300"
                  borderRadius="md"
                  p={6}
                  textAlign="center"
                  bg="gray.50"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: "teal.400",
                    bg: "teal.50",
                    cursor: "pointer",
                  }}
                  position="relative"
                >
                  <Input
                    type="file"
                    height="100%"
                    width="100%"
                    position="absolute"
                    top="0"
                    left="0"
                    opacity="0"
                    aria-hidden="true"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={handleFileChange}
                    cursor="pointer"
                  />
                  <VStack spacing={1}>
                    <Icon as={FiFile} w={6} h={6} color="gray.400" />
                    <Text fontSize="sm" color="gray.600">
                      Click to upload Payment Proof
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      (JPG, PNG or PDF • Max 10MB)
                    </Text>
                  </VStack>
                </Box>
              ) : (
                <Flex
                  align="center"
                  justify="space-between"
                  p={4}
                  border="1px solid"
                  borderColor="teal.200"
                  borderRadius="md"
                  bg="teal.50"
                >
                  <Flex align="center" gap={3}>
                    <Box
                      p={2}
                      borderRadius="full"
                      bg="teal.100"
                      color="teal.600"
                    >
                      <Icon as={FiFile} w={4} h={4} />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1} maxW="200px">
                        {fileName}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Ready to upload
                      </Text>
                    </Box>
                  </Flex>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={removeFile}
                    aria-label="Remove file"
                  >
                    <Icon as={FiX} />
                  </Button>
                </Flex>
              )}
            </FormControl>
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" bg="gray.50" py={4}>
          <Button variant="outline" mr={3} onClick={handleClose} bg="white">
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Updating..."
            px={8}
            shadow="md"
            display={accountDetails?.allowedTabs?.includes(routePath.paymentInitiateDocument) ? undefined : 'none'}
          >
            Update Payment
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default PaymentUtrDrawer;